import type { FeedbackSubmission } from './ontology/types';

/**
 * Query params allowed to survive feedback capture.
 *
 * PII posture: only explicitly benign params survive capture, regardless of
 * whether the current surface is authenticated; all other query params are dropped.
 */
const SEARCH_PARAM_ALLOWLIST = ['count', 'processing_started'] as const;

/** Minimal `URLSearchParams` shape (just `getAll`) needed to read incoming params. */
type SearchParamSource = Pick<URLSearchParams, 'getAll'>;

/** Returns a new `URLSearchParams` containing only the allowlisted keys from the input. */
function filterAllowedSearchParams(inputParams: SearchParamSource): URLSearchParams {
  const outputParams = new URLSearchParams();

  for (const key of SEARCH_PARAM_ALLOWLIST) {
    const values = inputParams.getAll(key);
    for (const value of values) {
      outputParams.append(key, value);
    }
  }

  return outputParams;
}

/** Formats the allowlisted params as a `?`-prefixed query string, or `''` when none remain. */
export function formatAllowedSearch(inputParams: SearchParamSource): string {
  const filteredSearch = filterAllowedSearchParams(inputParams).toString();

  return filteredSearch ? `?${filteredSearch}` : '';
}

/** Captures the current route as the pathname plus its allowlisted search string. */
export function captureRoute(): { pathname: string; search: string } {
  const { pathname, search } = window.location;

  return {
    pathname,
    search: formatAllowedSearch(new URLSearchParams(search)),
  };
}

/** Captures the browser environment (user agent, viewport size, color scheme) for a feedback report. */
export function captureEnv(mode: 'light' | 'dark'): FeedbackSubmission['env'] {
  return {
    user_agent: navigator.userAgent,
    viewport_w: window.innerWidth,
    viewport_h: window.innerHeight,
    color_scheme: mode,
  };
}
